import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    TextField,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Checkbox,
    CircularProgress,
    Alert,
    IconButton,
    Tooltip,
} from "@mui/material";
import { ArrowBack, Save, Restore, Visibility, PictureAsPdf } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import sailingService from "../../services/sailingService";

const fmt = (n) => (typeof n === "number" ? n.toFixed(2) : "0.00");

// Redistributes the OTHER customers' amounts so the full set still sums to
// exactly `totalFreight` after `editedId` is set to `newValue` — each other
// customer's share of the remaining amount is kept proportional to their
// share of what the others summed to before this edit (falls back to an
// even split if the others were all zero). This is what makes editing one
// customer's amount "automatically adjust" the rest, including the simple
// two-customer case (the other one just becomes total - edited).
const redistribute = (amounts, editedId, newValue, totalFreight) => {
    const clamped = Math.max(0, Math.min(totalFreight, newValue));
    const otherIds = Object.keys(amounts).filter((id) => id !== editedId);
    const remaining = Math.round((totalFreight - clamped) * 100) / 100;

    const next = { ...amounts, [editedId]: clamped };
    if (otherIds.length === 0) return next;

    const sumOthersOld = otherIds.reduce((s, id) => s + (amounts[id] || 0), 0);
    if (sumOthersOld > 0) {
        otherIds.forEach((id) => {
            next[id] = Math.round(remaining * ((amounts[id] || 0) / sumOthersOld) * 100) / 100;
        });
    } else {
        const even = Math.round((remaining / otherIds.length) * 100) / 100;
        otherIds.forEach((id) => {
            next[id] = even;
        });
    }

    // Floating-point rounding can leave the sum a cent or two off — push
    // the drift onto the last "other" customer so the total is always
    // exact, not just close.
    const sumNow = Object.values(next).reduce((s, v) => s + v, 0);
    const drift = Math.round((totalFreight - sumNow) * 100) / 100;
    if (drift !== 0 && otherIds.length > 0) {
        const lastId = otherIds[otherIds.length - 1];
        next[lastId] = Math.round((next[lastId] + drift) * 100) / 100;
    }

    return next;
};

// Pair-adjust mode: when exactly two customers are checked, editing one of
// them shifts the difference to only the OTHER checked customer — every
// other row stays untouched. This is what the admin actually wants when
// there are a lot of customers: `redistribute()` above ripples a tiny
// adjustment across every single row (technically correct, but "weird" to
// watch happen across 30 rows for what's really a two-customer correction).
// The pair's combined amount is held constant so the overall total is
// automatically preserved without touching anyone outside the pair.
const redistributePair = (amounts, editedId, newValue, otherId) => {
    const pairSumOld = (amounts[editedId] || 0) + (amounts[otherId] || 0);
    const clamped = Math.max(0, Math.min(pairSumOld, newValue));
    const otherNew = Math.round((pairSumOld - clamped) * 100) / 100;
    return { ...amounts, [editedId]: clamped, [otherId]: otherNew };
};

const AdminManifestFreightAllocation = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [allocation, setAllocation] = useState(null);
    const [amounts, setAmounts] = useState({}); // customerId -> number
    const [pairSelection, setPairSelection] = useState([]); // up to 2 customerIds
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [downloadingPdf, setDownloadingPdf] = useState(false);

    const load = () => {
        setLoading(true);
        setError("");
        sailingService
            .getFreightAllocation(id)
            .then(({ data }) => {
                const a = data.data.allocation;
                setAllocation(a);
                setAmounts(Object.fromEntries(a.customers.map((c) => [c.customerId, c.allocatedFreight])));
            })
            .catch((err) => setError(err.response?.data?.message || "Failed to load freight allocation"))
            .finally(() => setLoading(false));
    };

    useEffect(load, [id]);

    const handleAmountChange = (customerId, rawValue) => {
        if (!allocation) return;
        const value = rawValue === "" ? 0 : parseFloat(rawValue);
        if (Number.isNaN(value)) return;

        // If this customer is one of exactly two checked, only shift the
        // difference to its pair partner — everyone else stays put.
        if (pairSelection.length === 2 && pairSelection.includes(customerId)) {
            const otherId = pairSelection.find((pid) => pid !== customerId);
            setAmounts((prev) => redistributePair(prev, customerId, value, otherId));
            return;
        }

        setAmounts((prev) => redistribute(prev, customerId, value, allocation.totalFreight || 0));
    };

    const handleTogglePairSelection = (customerId) => {
        setPairSelection((prev) => {
            if (prev.includes(customerId)) return prev.filter((id) => id !== customerId);
            if (prev.length >= 2) {
                enqueueSnackbar("Only two customers can be paired at once — uncheck one first", {
                    variant: "info",
                });
                return prev;
            }
            return [...prev, customerId];
        });
    };

    const handleResetToWeightSplit = () => {
        if (!allocation) return;
        const totalWeight = allocation.customers.reduce((s, c) => s + (c.weightLb || 0), 0);
        const totalFreight = allocation.totalFreight || 0;
        if (totalWeight <= 0) {
            enqueueSnackbar("No weight recorded yet for these customers — can't compute a weight-based split", {
                variant: "warning",
            });
            return;
        }
        const next = {};
        allocation.customers.forEach((c) => {
            next[c.customerId] = Math.round(((c.weightLb || 0) / totalWeight) * totalFreight * 100) / 100;
        });
        // Fix rounding drift on the last customer, same as redistribute().
        const sumNow = Object.values(next).reduce((s, v) => s + v, 0);
        const drift = Math.round((totalFreight - sumNow) * 100) / 100;
        if (drift !== 0 && allocation.customers.length > 0) {
            const lastId = allocation.customers[allocation.customers.length - 1].customerId;
            next[lastId] = Math.round((next[lastId] + drift) * 100) / 100;
        }
        setAmounts(next);
    };

    const handleSave = async () => {
        if (!allocation) return;
        setSaving(true);
        try {
            const allocations = allocation.customers.map((c) => ({
                customerId: c.customerId,
                amount: amounts[c.customerId] || 0,
            }));
            const { data } = await sailingService.updateFreightAllocation(id, allocations);
            setAllocation(data.data.allocation);
            enqueueSnackbar(data.message || "Freight allocation saved", { variant: "success" });
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || "Failed to save freight allocation", {
                variant: "error",
            });
        } finally {
            setSaving(false);
        }
    };

    const handleDownloadPdf = async () => {
        setDownloadingPdf(true);
        try {
            const response = await sailingService.getManifestPdf(id);
            const disposition = response.headers?.["content-disposition"] || "";
            const filenameMatch = disposition.match(/filename="?([^"]+)"?/);
            const filename = filenameMatch ? filenameMatch[1] : "Manifest.pdf";

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            enqueueSnackbar("Failed to download manifest PDF", { variant: "error" });
        } finally {
            setDownloadingPdf(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    const totalFreight = allocation?.totalFreight ?? null;
    const sumAllocated = Object.values(amounts).reduce((s, v) => s + (v || 0), 0);
    const isBalanced = totalFreight != null && Math.abs(sumAllocated - totalFreight) < 0.01;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <IconButton size="small" onClick={() => navigate(-1)}>
                        <ArrowBack fontSize="small" />
                    </IconButton>
                    <Typography variant="h6" fontWeight={700}>
                        Freight Allocation — {allocation?.sailingNumber}
                    </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 1.5 }}>
                    <Button
                        variant="outlined"
                        startIcon={<Visibility />}
                        onClick={() => navigate(`/admin/sailing/${id}/manifest`)}
                    >
                        View Manifest
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={downloadingPdf ? <CircularProgress size={16} /> : <PictureAsPdf />}
                        onClick={handleDownloadPdf}
                        disabled={downloadingPdf}
                    >
                        Download PDF
                    </Button>
                </Box>
            </Box>

            {error && (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                    {error}
                </Alert>
            )}

            {!error && totalFreight == null && (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                    This sailing has no total freight amount on file yet — upload/confirm the Master BOL with its
                    charges section before allocating freight per customer.
                </Alert>
            )}

            {!error && allocation && (
                <Card>
                    <CardContent>
                        <Typography variant="body2" color="text.secondary" mb={0.5}>
                            Edit any customer's Allocated Freight below — the others automatically adjust to keep the
                            total equal to this sailing's actual freight bill (${fmt(totalFreight)}). Defaults to a
                            weight-proportional split until you save an override.
                        </Typography>
                        <Typography variant="body2" color="text.secondary" mb={2}>
                            With a lot of customers, check exactly two of them below to adjust just that pair —
                            editing one shifts the difference only to its checked partner, leaving everyone else's
                            amount untouched.
                        </Typography>

                        <Box sx={{ overflowX: "auto" }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox" />
                                        <TableCell>Customer</TableCell>
                                        <TableCell align="right">Weight (LB)</TableCell>
                                        <TableCell align="right">CBF</TableCell>
                                        <TableCell align="right">Total Cost ($)</TableCell>
                                        <TableCell align="right" sx={{ minWidth: 160 }}>
                                            Allocated Freight ($)
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {allocation.customers.map((c) => (
                                        <TableRow
                                            key={c.customerId}
                                            selected={pairSelection.includes(c.customerId)}
                                        >
                                            <TableCell padding="checkbox">
                                                <Tooltip title="Pair with another checked customer to adjust just the two of them">
                                                    <Checkbox
                                                        size="small"
                                                        checked={pairSelection.includes(c.customerId)}
                                                        onChange={() => handleTogglePairSelection(c.customerId)}
                                                        disabled={totalFreight == null}
                                                    />
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>{c.customerName}</TableCell>
                                            <TableCell align="right">{fmt(c.weightLb)}</TableCell>
                                            <TableCell align="right">{fmt(c.cbf)}</TableCell>
                                            <TableCell align="right">${fmt(c.totalCost)}</TableCell>
                                            <TableCell align="right">
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    value={amounts[c.customerId] ?? 0}
                                                    onChange={(e) => handleAmountChange(c.customerId, e.target.value)}
                                                    disabled={totalFreight == null}
                                                    slotProps={{ htmlInput: { min: 0, step: 0.01, style: { textAlign: "right" } } }}
                                                    sx={{ width: 140 }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mt: 2,
                                p: 1.5,
                                borderRadius: 2,
                                bgcolor: isBalanced ? "success.50" : "warning.50",
                                border: "1px solid",
                                borderColor: isBalanced ? "success.light" : "warning.light",
                            }}
                        >
                            <Typography variant="body2" fontWeight={600}>
                                Allocated total: ${fmt(sumAllocated)}
                                {totalFreight != null && ` of $${fmt(totalFreight)}`}
                            </Typography>
                            {!isBalanced && totalFreight != null && (
                                <Typography variant="caption" color="warning.dark">
                                    Doesn't reconcile — this shouldn't normally happen since amounts auto-adjust; try
                                    editing a value again.
                                </Typography>
                            )}
                        </Box>

                        <Box sx={{ display: "flex", gap: 1.5, justifyContent: "flex-end", mt: 2.5 }}>
                            <Tooltip title="Recompute every customer's amount from their weight share">
                                <Button variant="outlined" startIcon={<Restore />} onClick={handleResetToWeightSplit}>
                                    Reset to Weight-Based Split
                                </Button>
                            </Tooltip>
                            <Button
                                variant="contained"
                                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
                                onClick={handleSave}
                                disabled={saving || totalFreight == null || !isBalanced}
                            >
                                {saving ? "Saving…" : "Save Allocation"}
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default AdminManifestFreightAllocation;
